// ============================================================
// ComposeEmailModal.tsx
// Microsoft Stage 3 — compose + send an email AS the solicitor's own mailbox
// (Graph Mail.Send via POST /api/integrations/microsoft/send), logged as an
// 'email' activity on the case/client timeline. Launched from the client
// action toolbar's "Send email" button (which is connected-gated).
//
// Fields: To, Cc / Bcc (optional), Subject, With, rich-text Body (HTML).
//
// FRAME (approved mockup casevo-email-activity-modals.html, Option A): the
// shared ModalFrame at xl (896px) with `scrollBody` — the green hero band and
// the footer stay pinned, only the body scrolls. Body = surface cards (fields
// · editor · attachments) + options; footer = the compact TimerControl (the
// old BillingBar) on the left, Cancel · Save as draft · Send on the right.
// Esc / backdrop / ✕ / focus-trap are ModalFrame's; every close path goes
// through attemptClose (timer guard). ALL send / attachment / share-link /
// signature / timer / Microsoft / draft logic below is unchanged.
// On success: close + a success toast (parent) + the activity appears in the
// timeline (parent refetch). On needsConnect / needsReconnect: a prompt with a
// link into Settings (Microsoft account panel). On any other error: KEEP THE
// DRAFT and show the message — never lose the user's typing.
// Casevo-styled, light + dark. Chrome mirrors the app's other modals.
// ============================================================
import { registerTimerNavGuard } from '../../utils/timerNavGuard'
import TimerLeaveDialog from './TimerLeaveDialog'
import { NonBillableConfirm, NotBillableNote, isChargeableWithoutCase } from './NotBillable'
import { entryTypeFor } from '../../constants/entryTypes'
import { useLeavePageWarning } from '../../hooks/useLeavePageWarning'
import ActionBox from './ActionBox'
import React, { useEffect, useRef, useState } from 'react'
import { billableUnits, unitsToSeconds } from '../../utils/billingUnits'
import {
  sendOutlookMailApi,
  listFilesApi,
  fetchDocumentSearchApi,
  requestUploadUrlFlatApi,
  deleteFileFlatApi,
  createShareLinkApi,
  revokeShareLinkApi,
} from '../../api/casevoApi'
import {
  createEmailDraftApi,
  updateEmailDraftApi,
  deleteEmailDraftApi,
  type EmailDraft,
} from '../../api/casevoApi'
import { useCasevoStore } from '../../store/useCasevoStore'
import { navigateTo } from '../../navigation/navigate'
import { useDraftDirty, useUnsavedChangesGuard } from '../../hooks/useUnsavedChangesGuard'
import RichTextEditor from './RichTextEditor'
import { TimerControl } from './emailClientLayout'
import ModalFrame, { HERO_PILL_SOFT } from './ModalFrame'
import { BTN_PRIMARY } from '../../styles/buttons'

type MailPriority = 'low' | 'normal' | 'high'

// A pickable document, normalized from EITHER source: a case's files
// (listFilesApi) or a client's files (fetchDocumentSearchApi — files on the
// client's cases OR attached directly to the client). Both carry the fields
// the picker + budget need; caseLabel is shown only in the client view to
// disambiguate which case a document came from.
interface AttachableFile {
  id: string
  filename: string
  mimeType: string
  sizeBytes: number
  caseLabel?: string | null
}

// ── Attachment budgets (Stage B/D) — mirror the backend ──────────────
// Files up to the inline budget (~3MB) send fast (base64 in one message); files
// above that, up to the overall MAX, send via a Graph upload session (Stage D)
// — allowed, just slower. Only OVER the MAX is blocked. base64 inflates bytes
// by ~4/3, so the estimated on-the-wire total uses that factor.
const INLINE_ATTACHMENT_BUDGET_BYTES = 3 * 1024 * 1024
const MAX_TOTAL_SEND_BYTES = 35 * 1024 * 1024
// A file at/over this raw size is a candidate to send as a secure LINK (Stage 1)
// rather than attach — attaching it would push the email over the ~35MB cap.
const LARGE_FILE_SHARE_THRESHOLD_BYTES = 25 * 1024 * 1024
// Escape text for safe insertion into the HTML email body.
const escapeHtml = (s: string): string =>
  s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
// Base64 encodes 3 raw bytes as 4 chars → encoded size ≈ ceil(bytes/3)*4.
const estimateBase64Bytes = (rawBytes: number): number => Math.ceil(rawBytes / 3) * 4
// Human file size: "512 KB", "1.8 MB".
const formatBytes = (n: number): string => {
  if (n < 1024) return `${n} B`
  if (n < 1024 * 1024) return `${Math.round(n / 1024)} KB`
  return `${(n / (1024 * 1024)).toFixed(1)} MB`
}
// Clean line icons for the picker/chips — Feather/Lucide style (currentColor
// stroke), matching the app's inline-SVG icons (e.g. FileList). Muted tone via
// the caller's text colour. An image icon for images, a document icon for the
// rest; kept small (14px) and inline with the text.
const FileTypeIcon: React.FC<{ mimeType: string; className?: string }> = ({ mimeType, className }) => {
  const common = {
    width: 14,
    height: 14,
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: 2,
    strokeLinecap: 'round' as const,
    strokeLinejoin: 'round' as const,
    'aria-hidden': true,
    className: `shrink-0 ${className ?? ''}`,
  }
  if (mimeType.startsWith('image/')) {
    return (
      <svg {...common}>
        <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
        <circle cx="8.5" cy="8.5" r="1.5" />
        <polyline points="21 15 16 10 5 21" />
      </svg>
    )
  }
  return (
    <svg {...common}>
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
      <polyline points="14 2 14 8 20 8" />
    </svg>
  )
}

interface ComposeEmailModalProps {
  isOpen: boolean
  onClose: () => void
  clientId: string
  clientDisplayName: string
  caseId?: string | null
  // Pre-fill To when the client has an email on file (optional).
  defaultTo?: string | null
  // Called after a successful send so the parent can refetch the timeline
  // (the sent email lands as an 'email' activity) + show a success toast.
  onSent: () => void
  // Stage E — resume a saved draft: when set, the modal seeds every field from
  // it (and does NOT re-append the signature — the body already has it), and on
  // a successful SEND the draft is deleted (it's sent, no longer a draft).
  resumeDraft?: EmailDraft | null
  // Called after a draft is saved so the parent can refetch its drafts list +
  // show a "Draft saved" toast.
  onDraftSaved?: () => void
  // The case reference (SURNAME-YEAR-ID) for the header pill + the case-files
  // picker heading, when the email is tied to a case. Presentational only.
  caseRefLabel?: string | null
  // The client's cases → a Case picker (the client-wide Activity tab, where the
  // email isn't tied to one case yet). `caseId` is then only the starting
  // choice. Absent (the case page) → the case is fixed to `caseId`.
  // ★ Billable only on a case (./NotBillable): chargeable time with no case
  // shows the red "Not billable" note and asks before sending.
  cases?: { id: string; name: string; ref: string }[]
}

// The body is authored in the TipTap rich-text editor (bold/italic/lists/
// links/colour) and is already HTML — sent verbatim as Graph body
// contentType 'HTML' (an empty editor serializes to '<p></p>').
// (Clock + units helpers now live in emailClientLayout, shared with the
// AddActivity modal so the two billing readouts can't diverge.)

// Correspondent types — mirrors the standard AddActivityModal list so a logged
// email's correspondent reads the same ("Client: …", "Defendant: …"). Email
// defaults to Client (most client emails); the recipient(s) are the name.
const CORRESPONDENT_TYPES = [
  'Client', 'Defendant', 'Medical agency', 'Medical expert', 'Insurer',
  'GP', 'Hospital', 'Witness', 'Court', 'Other',
] as const

// Inline Feather line icons for the compose frame (design-language §6).
const Svg: React.FC<{ d: React.ReactNode; className?: string }> = ({ d, className = 'w-[15px] h-[15px]' }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" className={`shrink-0 ${className}`} aria-hidden="true">{d}</svg>
)
const I_MAIL = <><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" /><polyline points="22,6 12,13 2,6" /></>
const I_SEND = <><line x1="22" y1="2" x2="11" y2="13" /><polygon points="22 2 15 22 11 13 2 9 22 2" /></>
const I_SAVE = <><path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z" /><polyline points="17 21 17 13 7 13 7 21" /><polyline points="7 3 7 8 15 8" /></>
const I_CLIP = <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48" />
const I_FOLDER = <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" />
const I_UPLOAD = <><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="17 8 12 3 7 8" /><line x1="12" y1="3" x2="12" y2="15" /></>
const I_LINK = <><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" /><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" /></>
const I_X = <><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></>
const I_CHECK = <polyline points="20 6 9 17 4 12" />

const ComposeEmailModal: React.FC<ComposeEmailModalProps> = ({
  isOpen,
  onClose,
  clientId,
  clientDisplayName,
  caseId: caseIdProp,
  defaultTo,
  onSent,
  resumeDraft,
  onDraftSaved,
  caseRefLabel = null,
  cases,
}) => {
  // The case the email is logged on: picked (cases given) or fixed (prop).
  const [pickedCaseId, setPickedCaseId] = useState<string>(caseIdProp ?? '')
  const caseId: string | null = cases ? (pickedCaseId || null) : (caseIdProp ?? null)
  const shownCaseRef: string | null = cases ? (cases.find((c) => c.id === caseId)?.ref ?? null) : caseRefLabel
  // "Save as non-billable?" before sending chargeable time with no case.
  const [confirmNoCase, setConfirmNoCase] = useState(false)
  // The user's saved email signature (HTML), auto-appended to a NEW compose.
  const emailSignature = useCasevoStore((s) => s.authUser?.emailSignature ?? '')
  const [savingDraft, setSavingDraft] = useState(false)
  // Compose timer — mirrors the standard AddActivityModal's model so it has
  // the same Pause/Resume + manual-units controls. `currentSeconds` is the ONE
  // source of truth (clock, units card, and send's durationSeconds all read
  // it). `isTimerRunning` gates the drift-free tick; `manualOverride` freezes
  // the seconds→units sync while the user types units; the anchor lets the
  // tick recompute from wall-clock so pause holds and background tabs catch up.
  const timerAnchorRef = useRef<{ startedAtMs: number; baseSeconds: number } | null>(null)
  const [currentSeconds, setCurrentSeconds] = useState(0)
  // ★ Billable only on a case (./NotBillable): chargeable email time + no case.
  const notBillableNow = isChargeableWithoutCase(entryTypeFor('email').bills, currentSeconds, caseId)
  const [isTimerRunning, setIsTimerRunning] = useState(true)
  const [manualOverride, setManualOverride] = useState(false)
  const [unitsInput, setUnitsInput] = useState('0.0')

  const [to, setTo] = useState('')
  const [cc, setCc] = useState('')
  const [bcc, setBcc] = useState('')
  const [showBcc, setShowBcc] = useState(false)   // Outlook-style Bcc reveal
  const [subject, setSubject] = useState('')
  const [body, setBody] = useState('')            // HTML from the rich editor
  const [priority, setPriority] = useState<MailPriority>('normal')
  const [readReceipt, setReadReceipt] = useState(false)
  const [correspondentType, setCorrespondentType] = useState<string>('Client')
  // ── Attachments (Stage B) — pick from the case's OR client's documents ──
  // availableFiles is lazy-loaded the first time the picker opens: a case's
  // files when caseId is set, else the client's files (across their cases +
  // client-level docs). selectedFileIds are the ids sent to the backend; the
  // picker is an inline expandable panel toggled by showAttachPicker.
  const [availableFiles, setAvailableFiles] = useState<AttachableFile[]>([])
  const [filesLoaded, setFilesLoaded] = useState(false)
  const [filesLoading, setFilesLoading] = useState(false)
  const [filesError, setFilesError] = useState<string | null>(null)
  const [showAttachPicker, setShowAttachPicker] = useState(false)
  const [selectedFileIds, setSelectedFileIds] = useState<string[]>([])
  // Stage C — attach from device: upload the picked file(s) via the existing
  // presign flow (files them on the case/client), then select by id so they
  // attach exactly like a picked document. uploadingNames drives a per-file
  // "uploading…" hint; uploadError surfaces a rejected upload (bad type/size).
  const deviceInputRef = useRef<HTMLInputElement | null>(null)
  const [uploadingNames, setUploadingNames] = useState<string[]>([])
  const [uploadError, setUploadError] = useState<string | null>(null)
  // Share links (Stage 1) — a large file emailed as a secure link instead of an
  // attachment. sharedLinks tracks each created link (url + filename) so we can
  // wrap the URL as a real <a> in the sent body and note the filenames on the
  // logged activity. shareExpiryDays is the chosen lifetime (7/14/30).
  // id + fileId so a link can be CANCELLED before sending (revokeShareLinkApi);
  // line = the paragraph inserted into the body (removed again on cancel).
  const [sharedLinks, setSharedLinks] = useState<{ id: string; fileId: string; url: string; filename: string; line: string }[]>([])
  // ★ (Audit) Links made in this email die with it: closing WITHOUT sending or
  // saving a draft (discard / Esc / ✕ / leaving) revokes every link created
  // here, so no live link to a document is left behind in an email that was
  // never sent. Sent → the links stay; saved as a draft → kept (the draft's
  // body holds them). Best-effort: a failed revoke still closes (the link
  // can be cancelled from the document's share-links list).
  const sharedLinksRef = useRef(sharedLinks)
  sharedLinksRef.current = sharedLinks
  const revokeUnsentLinks = (): void => {
    const links = sharedLinksRef.current
    if (links.length === 0) return
    setSharedLinks([])
    for (const l of links) void revokeShareLinkApi(l.fileId, l.id).catch(() => {})
  }
  const [cancellingLinkId, setCancellingLinkId] = useState<string | null>(null)
  const [shareExpiryDays, setShareExpiryDays] = useState<7 | 14 | 30>(14)
  const [sharingId, setSharingId] = useState<string | null>(null)
  const [shareError, setShareError] = useState<string | null>(null)
  const [sending, setSending] = useState(false)
  const [error, setError] = useState<string | null>(null)
  // Connection prompt: 'connect' (never connected) | 'reconnect' (token dead).
  // Rendered as a gentle banner with a link into Settings; the draft is kept.
  const [connectPrompt, setConnectPrompt] = useState<'connect' | 'reconnect' | null>(null)

  // Reset on open. Two branches:
  //   RESUME a draft (resumeDraft set): seed every field from the draft. Do NOT
  //     re-append the signature — the saved body already contains it.
  //   NEW compose: seed To from the client's email, and append the user's
  //     signature after two blank lines (editable before send). No signature →
  //     blank body.
  useEffect(() => {
    if (isOpen) {
      // Shared resets (independent of branch).
      setAvailableFiles([])
      setFilesLoaded(false)
      setFilesLoading(false)
      setFilesError(null)
      setShowAttachPicker(false)
      setUploadingNames([])
      setUploadError(null)
      setSharedLinks([])
      setShareExpiryDays(14)
      setSharingId(null)
      setShareError(null)
      setSavingDraft(false)
      setSending(false)
      setError(null)
      setConnectPrompt(null)
      setManualOverride(false)
      timerAnchorRef.current = null
      // Starting case: the resumed draft's, else the one passed in.
      setPickedCaseId((resumeDraft ? resumeDraft.caseId : null) ?? caseIdProp ?? '')

      if (resumeDraft) {
        setTo(resumeDraft.to ?? '')
        setCc(resumeDraft.cc ?? '')
        setBcc(resumeDraft.bcc ?? '')
        setShowBcc(!!resumeDraft.bcc)
        setSubject(resumeDraft.subject ?? '')
        setBody(resumeDraft.bodyHtml ?? '')
        setPriority(
          resumeDraft.priority === 'low' || resumeDraft.priority === 'high' ? resumeDraft.priority : 'normal',
        )
        setReadReceipt(false)
        setCorrespondentType(resumeDraft.correspondentType || 'Client')
        setSelectedFileIds(resumeDraft.attachmentFileIds ?? [])
        // Continue accruing from the draft's saved compose time.
        setCurrentSeconds(resumeDraft.durationSeconds ?? 0)
        setIsTimerRunning(true)
        // Load the matter's files so the resumed attachment chips resolve.
        if ((resumeDraft.attachmentFileIds ?? []).length > 0) void loadFiles()
      } else {
        setTo(defaultTo ?? '')
        setCc('')
        setBcc('')
        setShowBcc(false)
        setSubject('')
        // Append the signature (HTML) after two blank paragraphs — editable.
        setBody(emailSignature ? `<p></p><p></p>${emailSignature}` : '')
        setPriority('normal')
        setReadReceipt(false)
        setCorrespondentType('Client')
        setSelectedFileIds([])
        setCurrentSeconds(0)
        setIsTimerRunning(true)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, defaultTo, resumeDraft, emailSignature])

  // ── DRIFT-FREE TIMER TICK ──────────────────────────────────
  // Mirrors AddActivityModal: recompute currentSeconds from a wall-clock
  // anchor rather than incrementing, so pause holds exactly and a throttled
  // background tab catches up. Anchored on the first tick of a run; paused by
  // clearing the anchor + flipping isTimerRunning (handlePauseTimer). Also
  // paused during send. currentSeconds is intentionally NOT a dep (only read
  // to seed the anchor; re-running each tick would re-anchor and drift).
  useEffect(() => {
    if (!isOpen || !isTimerRunning || sending) return
    if (!timerAnchorRef.current) {
      timerAnchorRef.current = { startedAtMs: Date.now(), baseSeconds: currentSeconds }
    }
    const id = window.setInterval(() => {
      const a = timerAnchorRef.current
      if (!a) return
      setCurrentSeconds(a.baseSeconds + Math.floor((Date.now() - a.startedAtMs) / 1000))
    }, 250)
    return () => window.clearInterval(id)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, isTimerRunning, sending])

  // ★ The compose timer (runs from open by default) had NO leave-page
  // warning at all — reload / tab close silently lost the billable time and
  // the unsent email. Prompt while compose is open with time running or
  // recorded; cleared once sending starts (the modal then closes).
  useLeavePageWarning(isOpen && !sending && (isTimerRunning || currentSeconds > 0))

  // Seconds → units display, unless the user is typing in the units box
  // (manualOverride). 6-minute units ROUNDED UP (utils/billingUnits) — the
  // rule the server charges, and the same as the standard modal.
  useEffect(() => {
    if (!manualOverride) setUnitsInput(String(billableUnits(currentSeconds)))
  }, [currentSeconds, manualOverride])

  // Pause/resume (mirrors AddActivityModal). Resume clears manualOverride so
  // the tick-sync resumes writing units, and clears the anchor so the next
  // tick re-anchors from the current (possibly overridden) currentSeconds.
  const handleStartTimer = (): void => {
    setManualOverride(false)
    timerAnchorRef.current = null
    setIsTimerRunning(true)
  }
  const handlePauseTimer = (): void => {
    timerAnchorRef.current = null
    setIsTimerRunning(false)
  }
  // Manual units override (same behaviour as before, now driven by the shared
  // BillingBar): any keystroke pauses the timer and takes ownership of
  // currentSeconds (units × 360s).
  const handleUnitsInputChange = (raw: string): void => {
    setUnitsInput(raw)
    setManualOverride(true)
    timerAnchorRef.current = null
    setIsTimerRunning(false)
    const parsed = parseFloat(raw)
    if (Number.isFinite(parsed) && parsed >= 0) {
      // Whole units (a fraction rounds UP) × 360s — charged as exactly these units.
      setCurrentSeconds(unitsToSeconds(parsed))
    } else if (raw.trim() === '') {
      setCurrentSeconds(0)
    }
  }

  // ── Attachments: lazy-load documents on first picker open ──
  // caseId present → that case's files (listFilesApi). Otherwise the client's
  // files (fetchDocumentSearchApi with clientId — matches files on the client's
  // cases OR attached directly to the client, firm-scoped server-side).
  const loadFiles = async (): Promise<void> => {
    if (filesLoaded || filesLoading) return
    if (!caseId && !clientId) return
    setFilesLoading(true)
    setFilesError(null)
    try {
      if (caseId) {
        const files = await listFilesApi(caseId)
        setAvailableFiles(
          files.map((f) => ({ id: f.id, filename: f.filename, mimeType: f.mimeType, sizeBytes: f.sizeBytes })),
        )
      } else {
        const docs = await fetchDocumentSearchApi({ clientId })
        setAvailableFiles(
          docs.map((d) => ({
            id: d.id,
            filename: d.filename,
            mimeType: d.mimeType,
            sizeBytes: d.sizeBytes,
            caseLabel: d.caseLabel,
          })),
        )
      }
      setFilesLoaded(true)
    } catch {
      setFilesError('Could not load documents.')
    } finally {
      setFilesLoading(false)
    }
  }
  const toggleAttachPicker = (): void => {
    const next = !showAttachPicker
    setShowAttachPicker(next)
    if (next) void loadFiles()
  }
  const toggleFileSelected = (id: string): void => {
    setSelectedFileIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]))
  }
  const removeSelected = (id: string): void => {
    setSelectedFileIds((prev) => prev.filter((x) => x !== id))
  }

  // ── Share as secure link (Stage 1) ────────────────────────────────────
  // Create an expiring, revocable share link for a large file, insert its URL
  // into the body (shown to the user; wrapped as an <a> at send time), and drop
  // the file from the attachment list. The URL survives as plain text in the
  // editor (no link mark needed); handleSend turns it into a clickable anchor.
  const shareAsLink = async (file: AttachableFile): Promise<void> => {
    if (sharingId) return
    setShareError(null)
    setSharingId(file.id)
    try {
      const { id, url, expiresAt } = await createShareLinkApi(file.id, shareExpiryDays)
      const dateLabel = new Date(expiresAt).toLocaleDateString('en-GB', {
        day: 'numeric', month: 'short', year: 'numeric',
      })
      // Append a labelled line to the body. The URL is plain text here; at send
      // time it's wrapped in an <a href>. escapeHtml guards the filename.
      const line = `<p>Download ${escapeHtml(file.filename)} — secure link (expires ${dateLabel}): ${url}</p>`
      setBody((prev) => (prev && prev !== '<p></p>' ? prev + line : line))
      setSelectedFileIds((prev) => prev.filter((x) => x !== file.id))
      setSharedLinks((prev) => [...prev, { id, fileId: file.id, url, filename: file.filename, line }])
    } catch {
      setShareError(`Could not create a share link for "${file.filename}". Please try again.`)
    } finally {
      setSharingId(null)
    }
  }

  // Cancel a share link made in this email (before sending): revoke it on the
  // server (it stops working at once) and take its line out of the body.
  const cancelSharedLink = async (link: { id: string; fileId: string; url: string; filename: string; line: string }): Promise<void> => {
    if (cancellingLinkId) return
    setShareError(null)
    setCancellingLinkId(link.id)
    try {
      await revokeShareLinkApi(link.fileId, link.id)
      setBody((prev) => {
        if (prev.includes(link.line)) return prev.replace(link.line, '')
        // Edited since — drop the paragraph that still holds the URL, else the URL itself.
        const para = new RegExp(`<p>(?:(?!</p>).)*${link.url.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}(?:(?!</p>).)*</p>`)
        return para.test(prev) ? prev.replace(para, '') : prev.split(link.url).join('')
      })
      setSharedLinks((prev) => prev.filter((l) => l.id !== link.id))
    } catch {
      setShareError(`Could not cancel the link for "${link.filename}". Please try again.`)
    } finally {
      setCancellingLinkId(null)
    }
  }

  // ── Attach from device (Stage C) ──────────────────────────────────────
  // Reuses the existing presign-upload flow: each picked file is FILED on the
  // case (caseId) or, absent a case, the client (clientId) as a client-level
  // document via requestUploadUrlFlatApi (creates the FileAttachment row +
  // returns a presigned PUT), then the bytes go straight to R2. On success the
  // new file id is added to availableFiles + selected, so it attaches to the
  // email through the SAME attachmentFileIds path as a picked document (and now
  // also lives on the matter — a real case/client document). Size is guarded by
  // the same ~3MB inline budget below; large device files are Stage D.
  const uploadDeviceFiles = async (files: File[]): Promise<void> => {
    if (files.length === 0) return
    if (!caseId && !clientId) return
    setUploadError(null)
    for (const file of files) {
      setUploadingNames((prev) => [...prev, file.name])
      let newFileId: string | null = null
      try {
        // Provision the row + presigned PUT. The flat route takes EITHER caseId
        // OR clientId (XOR) — file against the case if present, else the client.
        const provisioned = await requestUploadUrlFlatApi(
          caseId
            ? { caseId, filename: file.name, mimeType: file.type, sizeBytes: file.size }
            : { clientId, filename: file.name, mimeType: file.type, sizeBytes: file.size },
        )
        newFileId = provisioned.fileId
        // PUT the bytes DIRECTLY to R2 — ONLY Content-Type (extra headers would
        // break the presigned signature); plain fetch so no Bearer is added.
        const r2Response = await fetch(provisioned.uploadUrl, {
          method: 'PUT',
          // The type the server resolved (it fills in .msg / .eml the browser left blank).
          headers: { 'Content-Type': provisioned.mimeType ?? file.type },
          body: file,
        })
        if (!r2Response.ok) throw new Error(`Upload failed (status ${r2Response.status})`)
        // Filed successfully → make it a pickable + selected attachment.
        const attachable: AttachableFile = {
          id: provisioned.fileId,
          filename: file.name,
          mimeType: file.type,
          sizeBytes: file.size,
        }
        setAvailableFiles((prev) =>
          prev.some((f) => f.id === attachable.id) ? prev : [attachable, ...prev],
        )
        setSelectedFileIds((prev) => (prev.includes(attachable.id) ? prev : [...prev, attachable.id]))
      } catch (uploadErr) {
        // If the row was provisioned but the PUT failed, wipe the orphan
        // (best-effort; firm-scoped by id). Surface the backend's message when
        // present (e.g. unsupported type / too large from the upload-url route).
        if (newFileId !== null) {
          try {
            await deleteFileFlatApi(newFileId)
          } catch {
            /* best-effort orphan cleanup */
          }
        }
        const backendMsg =
          uploadErr && typeof uploadErr === 'object' && 'response' in uploadErr
            ? (uploadErr as { response?: { data?: { error?: string } } }).response?.data?.error
            : undefined
        setUploadError(backendMsg || `Could not attach "${file.name}". Please try again.`)
      } finally {
        setUploadingNames((prev) => prev.filter((n) => n !== file.name))
      }
    }
  }
  const handleDevicePick = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const picked = e.target.files ? Array.from(e.target.files) : []
    // Reset the input so re-picking the same file fires onChange again.
    e.target.value = ''
    void uploadDeviceFiles(picked)
  }

  // ★ Casevo "timer running" popup (TimerLeaveDialog) for IN-APP leaving.
  // The compose timer starts on open, so we only ask once there's billable
  // time (≥ 0.1 unit = 36s) — opening and immediately closing compose closes
  // normally. proceed/cancel come from an in-app navigation (browser Back via
  // timerNavGuard) or a follow-on action (e.g. "Go to Settings").
  const [leavePrompt, setLeavePrompt] = useState<{ proceed?: () => void; cancel?: () => void } | null>(null)
  const needsLeavePrompt = !sending && !savingDraft && currentSeconds >= 36
  useEffect(() => {
    if (!isOpen || !needsLeavePrompt) return
    return registerTimerNavGuard((proceed, cancel) => setLeavePrompt({ proceed, cancel }))
  }, [isOpen, needsLeavePrompt])
  // Unsaved-changes guard for what was TYPED (the timer popup above covers
  // recorded time): recipients / subject / message / attachments changed since
  // compose opened. The message is compared as text, so the editor's own HTML
  // normalising of the seeded signature never counts as a change. While the
  // timer popup applies it takes over (one prompt, not two).
  const typedDirty = useDraftDirty(isOpen, [to, cc, bcc, subject, body.replace(/<[^>]*>/g, '').trim(), selectedFileIds])
  const guardTyped = useUnsavedChangesGuard(isOpen && typedDirty && !sending && !savingDraft && !needsLeavePrompt)
  // Every close path (✕, Cancel, backdrop, Esc, Settings link) goes through
  // here. `after` runs once the modal has actually closed.
  const attemptClose = (after?: () => void): void => {
    if (sending || savingDraft) return
    if (leavePrompt) return
    if (needsLeavePrompt) { setLeavePrompt({ proceed: after }); return }
    guardTyped(() => {
      revokeUnsentLinks()
      onClose()
      after?.()
    })
  }
  // (Esc / backdrop / ✕ / focus-trap now come from ModalFrame — every close
  // path routes through attemptClose via its onClose, so the timer-guard is
  // unchanged. The modal's own overlay + window Esc listener were removed.)

  if (!isOpen) return null

  // ── Attachment budget (derived) ───────────────────────────────────────
  // The selected files' metadata (for chips + totals) and whether they bust
  // the ~3MB inline budget. Estimate mirrors the backend: base64 sum of the
  // files + the body's byte length vs INLINE_ATTACHMENT_BUDGET_BYTES.
  const selectedFiles = selectedFileIds
    .map((id) => availableFiles.find((f) => f.id === id))
    .filter((f): f is AttachableFile => !!f)
  const rawAttachedBytes = selectedFiles.reduce((sum, f) => sum + f.sizeBytes, 0)
  const estimatedSendBytes =
    selectedFiles.reduce((sum, f) => sum + estimateBase64Bytes(f.sizeBytes), 0) +
    new TextEncoder().encode(body).length
  // Over the overall MAX → blocked. Between the inline budget and the MAX →
  // allowed via an upload session (slower); we hint that when sending.
  const overBudget = estimatedSendBytes > estimateBase64Bytes(MAX_TOTAL_SEND_BYTES)
  const usesUploadSession = estimatedSendBytes > INLINE_ATTACHMENT_BUDGET_BYTES

  // nonBillableOk: the user already confirmed sending chargeable time with no case.
  const handleSend = async (opts: { nonBillableOk?: boolean } = {}): Promise<void> => {
    if (sending) return
    setError(null)
    setConnectPrompt(null)
    if (to.trim().length === 0) { setError('Add at least one recipient.'); return }
    if (subject.trim().length === 0) { setError('Add a subject.'); return }
    // Don't send while a device file is still uploading/filing, or a share link
    // is being created.
    if (uploadingNames.length > 0) { setError('Wait for attachments to finish uploading.'); return }
    if (sharingId !== null) { setError('Wait for the share link to finish.'); return }
    // Block only when over the overall MAX (the backend re-guards with
    // { tooLarge }). Files between the inline budget and the MAX are fine — they
    // go via an upload session.
    if (overBudget) { setError('Attachments are too large to email (about 35 MB total). Remove some files and try again.'); return }
    // Chargeable time but no case → ask first (it won't be billable). A saved
    // DRAFT isn't asked — nothing is logged until it's sent.
    if (!opts.nonBillableOk && notBillableNow) { setConfirmNoCase(true); return }
    // Freeze the timer for send — the accrued/overridden currentSeconds is the
    // billed duration (excludes any paused time; honours a manual override).
    setIsTimerRunning(false)
    timerAnchorRef.current = null
    setSending(true)
    // Wrap each share URL (plain text in the editor) as a real <a href> in the
    // SENT body, so the recipient gets a clickable link. The tokens are unique +
    // unguessable, so a plain string replace is safe.
    let bodyHtmlToSend = body
    for (const l of sharedLinks) {
      bodyHtmlToSend = bodyHtmlToSend
        .split(l.url)
        .join(`<a href="${l.url}">${l.url}</a>`)
    }
    const result = await sendOutlookMailApi({
      to: to.trim(),
      cc: cc.trim() || undefined,
      bcc: bcc.trim() || undefined,
      subject: subject.trim(),
      bodyHtml: bodyHtmlToSend, // HTML from the rich editor, share URLs anchored
      priority,
      readReceipt,
      // Accrued/overridden compose time → billed on the logged activity,
      // editable afterward on the timeline like any other activity's time.
      durationSeconds: currentSeconds,
      correspondentType,
      // Selected case documents → attached inline server-side (firm-scoped +
      // base64'd there). Omit the key entirely when nothing is attached.
      attachmentFileIds: selectedFileIds.length ? selectedFileIds : undefined,
      // Files sent as a secure LINK instead — noted on the logged activity.
      sharedFilenames: sharedLinks.length ? sharedLinks.map((l) => l.filename) : undefined,
      clientId,
      caseId: caseId ?? null,
    })
    setSending(false)
    if (result.ok) {
      // Resumed from a draft → it's sent now, so delete the draft (best-effort).
      if (resumeDraft?.id) void deleteEmailDraftApi(resumeDraft.id)
      onSent()   // parent: refetch timeline + success toast
      onClose()
      return
    }
    // Draft is preserved on every failure path.
    if (result.needsConnect) { setConnectPrompt('connect'); return }
    if (result.needsReconnect) { setConnectPrompt('reconnect'); return }
    // Backend backstop for the size budget (shouldn't fire — we block above).
    if (result.tooLarge) { setError(result.error ?? 'Attachments are too large to send inline.'); return }
    setError(result.error ?? 'Could not send the email. Please try again.')
  }

  // Save the current compose as a draft (create, or update when resuming an
  // existing one). Persists ALL compose fields incl. the elapsed compose time +
  // selected attachment ids. Then closes + lets the parent refresh + toast.
  const handleSaveDraft = async (): Promise<boolean> => {
    if (savingDraft || sending) return false
    setError(null)
    // Freeze the timer like send — the accrued seconds are saved on the draft.
    setIsTimerRunning(false)
    timerAnchorRef.current = null
    setSavingDraft(true)
    const input = {
      to: to.trim() || undefined,
      cc: cc.trim() || undefined,
      bcc: bcc.trim() || undefined,
      subject: subject.trim() || undefined,
      bodyHtml: body || undefined,
      correspondentType,
      priority,
      durationSeconds: currentSeconds,
      attachmentFileIds: selectedFileIds,
      caseId: caseId ?? null,
      clientId,
    }
    try {
      if (resumeDraft?.id) {
        await updateEmailDraftApi(resumeDraft.id, input)
      } else {
        await createEmailDraftApi(input)
      }
      onDraftSaved?.()
      onClose()
      return true
    } catch {
      setError('Could not save the draft. Please try again.')
      return false
    } finally {
      setSavingDraft(false)
    }
  }

  const goToSettings = (): void => {
    attemptClose(() => { void navigateTo({ view: 'settings', params: { section: 'email' } }) })
  }
  // TimerLeaveDialog choices. "Save" for email = save as DRAFT (the draft
  // keeps the recorded time; sending isn't something to do on the way out).
  const handleLeaveSave = async (): Promise<void> => {
    const p = leavePrompt
    setLeavePrompt(null)
    const ok = await handleSaveDraft()
    if (ok) p?.proceed?.()
    else p?.cancel?.()
  }
  const handleLeaveDiscard = (): void => {
    const p = leavePrompt
    setLeavePrompt(null)
    timerAnchorRef.current = null
    setIsTimerRunning(false)
    revokeUnsentLinks()
    onClose()
    p?.proceed?.()
  }
  const handleLeaveKeep = (): void => {
    const p = leavePrompt
    setLeavePrompt(null)
    p?.cancel?.()
  }

  // ── Presentational constants (approved mockup, Option A) ──
  const CARD = 'bg-casevo-surface border border-casevo-border rounded-[14px]'
  const ROW = 'flex items-center gap-[14px] px-4 min-h-[46px] border-t border-casevo-border-soft first:border-t-0'
  const ROW_LABEL = 'w-16 shrink-0 text-[10.5px] font-semibold tracking-[0.16em] uppercase text-casevo-text-soft'
  const ROW_INPUT = 'flex-1 min-w-0 bg-transparent border-0 p-0 py-3 text-[14px] text-casevo-forest placeholder:text-casevo-text-soft focus:outline-none disabled:opacity-60 disabled:cursor-not-allowed'
  const SELECT = 'border border-casevo-border rounded-[8px] px-[10px] py-[5px] text-[13px] bg-casevo-input text-casevo-forest focus:outline-none focus:border-casevo-mid disabled:opacity-60'
  const MBTN = 'inline-flex items-center gap-[6px] text-[12.5px] font-semibold rounded-[8px] px-[10px] py-[5px] border transition-colors focus:outline-none casevo-focus disabled:opacity-60 disabled:cursor-not-allowed'
  const FBTN = 'inline-flex items-center gap-[7px] text-[14px] rounded-[8px] px-4 py-[10px] border transition-colors focus:outline-none casevo-focus disabled:opacity-60 disabled:cursor-not-allowed'

  const draftSaved = resumeDraft?.updatedAt
    ? new Date(resumeDraft.updatedAt).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })
    : null
  const sendDisabled = sending || savingDraft || overBudget || uploadingNames.length > 0 || sharingId !== null

  return (
    <>
    <ModalFrame
      isOpen
      onClose={() => { if (!sending) attemptClose() }}
      dismissible={!sending}
      size="xl"
      scrollBody
      eyebrow="Send email"
      eyebrowIcon={<Svg d={I_MAIL} className="w-3.5 h-3.5" />}
      pills={
        <>
          {shownCaseRef && <span className={HERO_PILL_SOFT}>{shownCaseRef}</span>}
          {resumeDraft && <span className={`${HERO_PILL_SOFT} !font-sans`}>Draft{draftSaved ? ` · saved ${draftSaved}` : ''}</span>}
        </>
      }
      title={`Email to ${clientDisplayName}`}
      subtitle={caseId
        ? 'Sent from your Outlook mailbox and logged as an email on this case’s activity'
        : 'Sent from your Outlook mailbox and logged as an email on this client’s activity'}
      bodyClassName="flex flex-col gap-[14px]"
      footer={
        <div className="flex flex-wrap items-center gap-[10px] w-full">
          {/* Compact billing control (was the full-width BillingBar). */}
          <TimerControl
            currentSeconds={currentSeconds}
            isTimerRunning={isTimerRunning}
            onToggleTimer={isTimerRunning ? handlePauseTimer : handleStartTimer}
            unitsInput={unitsInput}
            onUnitsInputChange={handleUnitsInputChange}
            disabled={sending}
          />
          <div className="ml-auto flex items-center gap-[10px]">
            <button
              type="button"
              onClick={() => { if (!sending && !savingDraft) attemptClose() }}
              disabled={sending || savingDraft}
              className={`${FBTN} border-casevo-border bg-transparent text-casevo-mid font-medium hover:border-casevo-mid hover:text-casevo-forest`}
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={() => { void handleSaveDraft() }}
              disabled={sending || savingDraft || uploadingNames.length > 0 || sharingId !== null}
              className={`${FBTN} border-casevo-border bg-casevo-surface text-casevo-brand dark:text-[#8fd6b0] font-semibold hover:border-casevo-mid`}
            >
              <Svg d={I_SAVE} />{savingDraft ? 'Saving…' : 'Save as draft'}
            </button>
            <button
              type="button"
              onClick={() => { void handleSend() }}
              disabled={sendDisabled}
              title={overBudget ? 'Attachments exceed the ~35 MB limit' : undefined}
              className={`${BTN_PRIMARY} text-[14px] rounded-[8px] px-4 py-[10px] border border-casevo-primary`}
            >
              <Svg d={I_SEND} />{sending ? 'Sending…' : 'Send email'}
            </button>
          </div>
        </div>
      }
    >
      {/* Connection prompt — draft kept; link into Settings. */}
      {connectPrompt && (
        // (Audit fix batch 2) The shared note shell, Open Settings as its one step.
        <ActionBox alert className="shrink-0"
          title={connectPrompt === 'connect' ? 'Connect your Microsoft account first to send email' : 'Your Microsoft connection expired — reconnect to send'}
          message="Your email is kept as it is."
          actions={[{ label: 'Open Settings', kind: 'primary', onClick: goToSettings }]} />
      )}

      {/* Fields card — To / Cc / (Bcc) / Subject / With. */}
      <div className={`${CARD} shrink-0`}>
        <label className={ROW}>
          <span className={ROW_LABEL}>To</span>
          <input
            type="text"
            value={to}
            onChange={(e) => setTo(e.target.value)}
            disabled={sending}
            placeholder="name@example.com (comma-separate for several)"
            className={ROW_INPUT}
          />
          {!showBcc && (
            <button
              type="button"
              onClick={() => setShowBcc(true)}
              disabled={sending}
              className="shrink-0 text-[12px] font-semibold text-casevo-brand dark:text-[#8fd6b0] hover:underline focus:outline-none casevo-focus rounded-sm"
            >
              + Bcc
            </button>
          )}
        </label>
        <label className={ROW}>
          <span className={ROW_LABEL}>Cc</span>
          <input
            type="text"
            value={cc}
            onChange={(e) => setCc(e.target.value)}
            disabled={sending}
            placeholder="Name or address — comma-separate for several"
            className={ROW_INPUT}
          />
        </label>
        {showBcc && (
          <label className={ROW}>
            <span className={ROW_LABEL}>Bcc</span>
            <input
              type="text"
              value={bcc}
              onChange={(e) => setBcc(e.target.value)}
              disabled={sending}
              placeholder="bcc@example.com (comma-separate for several)"
              className={ROW_INPUT}
            />
          </label>
        )}
        <label className={ROW}>
          <span className={ROW_LABEL}>Subject</span>
          <input
            type="text"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            disabled={sending}
            placeholder="Subject"
            className={`${ROW_INPUT} !text-[14.5px] font-semibold`}
          />
        </label>
        <div className={`${ROW} flex-wrap py-2`}>
          <span className={ROW_LABEL}>With</span>
          <select
            value={correspondentType}
            onChange={(e) => setCorrespondentType(e.target.value)}
            disabled={sending}
            aria-label="Correspondent type"
            className={SELECT}
          >
            {CORRESPONDENT_TYPES.map((t) => (
              <option key={t} value={t}>{t}</option>
            ))}
          </select>
          <span className="text-[12px] text-casevo-text-soft">Logged on the timeline as “Email · {correspondentType}”</span>
        </div>
        {/* Case — a picker on the client-wide tab (cases given), fixed on a case
            page. Time is billable only on a case (./NotBillable). */}
        {cases ? (
          <label className={`${ROW} flex-wrap py-2`}>
            <span className={ROW_LABEL}>Case</span>
            <select
              value={pickedCaseId}
              onChange={(e) => {
                setPickedCaseId(e.target.value)
                // The attach picker lists the chosen case's (or client's) files.
                setAvailableFiles([]); setFilesLoaded(false); setFilesError(null)
              }}
              disabled={sending}
              aria-invalid={notBillableNow || undefined}
              aria-describedby={notBillableNow ? 'email-case-not-billable' : undefined}
              className={`${SELECT} flex-1 min-w-0 ${notBillableNow ? '!border-casevo-danger' : ''}`}
            >
              <option value="">No specific case</option>
              {cases.map((c) => <option key={c.id} value={c.id}>{c.name} · {c.ref}</option>)}
            </select>
            {notBillableNow && <NotBillableNote id="email-case-not-billable" className="basis-full pl-[78px] -mt-1 pb-1" />}
          </label>
        ) : notBillableNow && (
          <div className={`${ROW} flex-wrap py-2`}>
            <span className={ROW_LABEL}>Case</span>
            <span className="text-[14px] text-casevo-text-soft">No specific case</span>
            <NotBillableNote id="email-case-not-billable" className="basis-full pl-[78px] -mt-1 pb-1" />
          </div>
        )}
      </div>

      {/* Editor card — RichTextEditor (its own toolbar strip on top). */}
      <div className="shrink-0 [&>div]:!rounded-[14px] [&_.ProseMirror]:!min-h-[280px]">
        <RichTextEditor value={body} onChange={setBody} enableImages cleanBody />
      </div>

      {/* Attachments card. */}
      {(caseId || clientId) && (
        <div className={`${CARD} shrink-0 px-4 py-3`}>
          <div className="flex flex-wrap items-center gap-2 text-[13.5px]">
            <span className="text-casevo-brand dark:text-[#8fd6b0]"><Svg d={I_CLIP} /></span>
            <b className="font-semibold text-casevo-forest">Attachments</b>
            {selectedFiles.length > 0 && (
              <span className="text-[12.5px] text-casevo-text-soft tabular-nums">
                {selectedFiles.length} file{selectedFiles.length > 1 ? 's' : ''} · {formatBytes(rawAttachedBytes)}
              </span>
            )}
            <span className="flex-1" />
            <button
              type="button"
              onClick={toggleAttachPicker}
              disabled={sending}
              aria-expanded={showAttachPicker}
              className={`${MBTN} ${showAttachPicker
                ? 'bg-casevo-mint text-casevo-brand dark:text-[#8fd6b0] border-transparent'
                : 'bg-casevo-surface text-casevo-brand dark:text-[#8fd6b0] border-casevo-border hover:border-casevo-mid'}`}
            >
              <Svg d={I_FOLDER} className="w-[14px] h-[14px]" />
              {showAttachPicker ? 'Done' : caseId ? 'From case files' : 'From client files'}
            </button>
            <button
              type="button"
              onClick={() => deviceInputRef.current?.click()}
              disabled={sending || uploadingNames.length > 0}
              className={`${MBTN} bg-casevo-surface text-casevo-brand dark:text-[#8fd6b0] border-casevo-border hover:border-casevo-mid`}
            >
              <Svg d={I_UPLOAD} className="w-[14px] h-[14px]" />From device
            </button>
            <input
              ref={deviceInputRef}
              type="file"
              multiple
              onChange={handleDevicePick}
              className="hidden"
              aria-hidden="true"
            />
          </div>

          {selectedFiles.length > 0 && (
            <div className="flex flex-wrap gap-2 mt-[11px]">
              {selectedFiles.map((f) => {
                const big = f.sizeBytes >= LARGE_FILE_SHARE_THRESHOLD_BYTES
                return (
                  <span
                    key={f.id}
                    className={`inline-flex items-center gap-[7px] max-w-full rounded-[9px] border pl-[10px] pr-2 py-[6px] text-[12.5px] text-casevo-forest ${big
                      ? 'border-[color-mix(in_srgb,#8a6212_55%,var(--casevo-border))] bg-[#fdf3dc] dark:border-[color-mix(in_srgb,#dcbb72_55%,var(--casevo-border))] dark:bg-[rgba(214,170,80,0.14)]'
                      : 'border-casevo-border bg-casevo-input'}`}
                  >
                    <FileTypeIcon mimeType={f.mimeType} className="text-casevo-mid" />
                    <span className="truncate max-w-[240px]" title={f.filename}>{f.filename}</span>
                    <span className="text-casevo-text-soft tabular-nums">{formatBytes(f.sizeBytes)}</span>
                    {big && (
                      <button
                        type="button"
                        onClick={() => { void shareAsLink(f) }}
                        disabled={sending || sharingId !== null}
                        title="Too large to attach — send a secure download link instead"
                        className="inline-flex items-center gap-[5px] rounded-full bg-casevo-primary hover:bg-casevo-primary-hover text-white px-[9px] py-[3px] text-[11px] font-bold transition-colors focus:outline-none casevo-focus disabled:opacity-60 disabled:cursor-not-allowed"
                      >
                        <Svg d={I_LINK} className="w-[11px] h-[11px]" />
                        {sharingId === f.id ? 'Sharing…' : 'Share as link'}
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={() => removeSelected(f.id)}
                      disabled={sending}
                      aria-label={`Remove ${f.filename}`}
                      className="w-5 h-5 rounded-[5px] grid place-items-center text-casevo-text-soft hover:text-casevo-forest hover:bg-casevo-border-soft focus:outline-none casevo-focus disabled:opacity-60"
                    >
                      <Svg d={I_X} className="w-3 h-3" />
                    </button>
                  </span>
                )
              })}
            </div>
          )}

          {showAttachPicker && (
            <div className="mt-[11px] border border-casevo-border rounded-[11px] overflow-hidden">
              <div className="flex justify-between gap-3 px-3 py-2 bg-casevo-input text-[11px] tracking-[0.12em] uppercase font-semibold text-casevo-text-soft">
                <span>{caseId ? 'Case files' : 'Client files'}{shownCaseRef && caseId ? ` · ${shownCaseRef}` : ''}</span>
                {!filesLoading && !filesError && <span className="normal-case tracking-normal font-medium tabular-nums">{availableFiles.length} document{availableFiles.length === 1 ? '' : 's'}</span>}
              </div>
              <div className="max-h-52 overflow-y-auto">
                {filesLoading && <p className="text-[12.5px] text-casevo-text-soft px-3 py-2">Loading documents…</p>}
                {filesError && <p className="text-[12.5px] text-casevo-danger px-3 py-2">{filesError}</p>}
                {!filesLoading && !filesError && availableFiles.length === 0 && (
                  <p className="text-[12.5px] text-casevo-text-soft px-3 py-2">
                    {caseId ? 'No documents on this case yet.' : 'No documents for this client yet.'}
                  </p>
                )}
                {!filesLoading &&
                  !filesError &&
                  availableFiles.map((f) => {
                    const on = selectedFileIds.includes(f.id)
                    return (
                      <label
                        key={f.id}
                        className={`relative flex items-center gap-[10px] px-3 py-2 border-t border-casevo-border-soft first:border-t-0 text-[13px] cursor-pointer hover:bg-casevo-input ${on ? 'bg-[color-mix(in_srgb,var(--casevo-mint)_45%,transparent)]' : ''}`}
                      >
                        <input
                          type="checkbox"
                          checked={on}
                          onChange={() => toggleFileSelected(f.id)}
                          disabled={sending}
                          className="sr-only peer"
                        />
                        <span aria-hidden="true" className={`w-4 h-4 rounded-[4px] grid place-items-center shrink-0 border-[1.5px] peer-focus-visible:ring-2 peer-focus-visible:ring-[var(--casevo-focus)] ${on ? 'bg-casevo-primary border-casevo-primary text-white' : 'border-casevo-border'}`}>
                          {on && <Svg d={I_CHECK} className="w-[11px] h-[11px]" />}
                        </span>
                        <FileTypeIcon mimeType={f.mimeType} className="text-casevo-mid" />
                        <span className="min-w-0 flex-1">
                          <span className="text-casevo-forest truncate block" title={f.filename}>{f.filename}</span>
                          {!caseId && f.caseLabel && (
                            <span className="text-[11px] text-casevo-text-soft truncate block" title={f.caseLabel}>{f.caseLabel}</span>
                          )}
                        </span>
                        <span className="text-[12px] text-casevo-text-soft tabular-nums shrink-0">{formatBytes(f.sizeBytes)}</span>
                      </label>
                    )
                  })}
              </div>
            </div>
          )}

          {(selectedFiles.some((f) => f.sizeBytes >= LARGE_FILE_SHARE_THRESHOLD_BYTES) || uploadingNames.length > 0) && (
            <div className="flex flex-wrap items-center gap-2 mt-[10px] text-[12.5px] text-casevo-text-soft">
              {selectedFiles.some((f) => f.sizeBytes >= LARGE_FILE_SHARE_THRESHOLD_BYTES) && (
                <label className="inline-flex items-center gap-2">
                  <Svg d={I_LINK} className="w-[13px] h-[13px]" />
                  <span>Share links expire in</span>
                  <select
                    value={shareExpiryDays}
                    onChange={(e) => setShareExpiryDays(Number(e.target.value) as 7 | 14 | 30)}
                    disabled={sending || sharingId !== null}
                    aria-label="Share link expiry"
                    className={`${SELECT} !text-[12px] !py-[3px] !px-2`}
                  >
                    <option value={7}>7 days</option>
                    <option value={14}>14 days</option>
                    <option value={30}>30 days</option>
                  </select>
                </label>
              )}
              {uploadingNames.length > 0 && (
                <span className="ml-auto inline-flex items-center gap-[6px] text-casevo-mid" aria-live="polite">
                  <Svg d={I_UPLOAD} className="w-[13px] h-[13px]" />Uploading {uploadingNames.join(', ')}…
                </span>
              )}
            </div>
          )}
          {uploadError && <p className="mt-2 text-[12.5px] text-casevo-danger">{uploadError}</p>}
          {shareError && <p className="mt-2 text-[12.5px] text-casevo-danger">{shareError}</p>}
          {/* Links made in this email — each can be cancelled before sending. */}
          {sharedLinks.length > 0 && (
            <ul aria-label="Secure links in this email" className="mt-[10px] flex flex-wrap gap-[6px] list-none p-0">
              {sharedLinks.map((l) => (
                <li key={l.id} className="inline-flex items-center gap-[6px] h-[28px] pl-[10px] pr-[4px] rounded-full border border-casevo-border bg-casevo-surface text-[12.5px] text-casevo-forest">
                  <span className="truncate max-w-[220px]" title={l.filename}>Link: {l.filename}</span>
                  <button type="button" onClick={() => { void cancelSharedLink(l) }} disabled={cancellingLinkId === l.id}
                    className="h-[22px] px-[8px] rounded-full text-[12px] font-semibold text-casevo-danger hover:bg-casevo-danger-bg focus:outline-none casevo-focus disabled:opacity-50">
                    {cancellingLinkId === l.id ? 'Cancelling…' : 'Cancel link'}
                  </button>
                </li>
              ))}
            </ul>
          )}

          {selectedFiles.length > 0 && (
            <div className={`mt-[10px] text-[12px] tabular-nums flex flex-wrap items-center justify-between gap-x-3 gap-y-1 ${overBudget ? 'text-[#c23b2c] dark:text-[#f3a094]' : 'text-casevo-text-soft'}`}>
              <span>
                {selectedFiles.length} file{selectedFiles.length > 1 ? 's' : ''} · {formatBytes(rawAttachedBytes)} attached
              </span>
              {overBudget ? (
                <span className="text-right">Too large to email (about 35 MB max). Remove some files and try again.</span>
              ) : usesUploadSession ? (
                <span className="text-right">Large attachment — sending may take a moment.</span>
              ) : null}
            </div>
          )}
        </div>
      )}

      {/* Options — priority (green-selected segmented) + read receipt (switch). */}
      <div className="shrink-0 flex flex-wrap items-center gap-[10px] px-1">
        <span className="text-[10.5px] font-semibold tracking-[0.16em] uppercase text-casevo-text-soft" id="compose-priority-label">Priority</span>
        <div role="radiogroup" aria-labelledby="compose-priority-label" className="inline-flex border border-casevo-border rounded-[8px] overflow-hidden text-[12.5px]">
          {(['low', 'normal', 'high'] as const).map((p) => (
            <button
              key={p}
              type="button"
              role="radio"
              aria-checked={priority === p}
              onClick={() => setPriority(p)}
              disabled={sending}
              className={`px-3 py-[5px] border-l border-casevo-border first:border-l-0 capitalize focus:outline-none casevo-focus disabled:opacity-60 ${priority === p ? 'bg-casevo-primary text-white font-semibold' : 'text-casevo-mid hover:bg-casevo-mint/30'}`}
            >
              {p}
            </button>
          ))}
        </div>
        <span className="flex-1" />
        <label className="relative inline-flex items-center gap-[9px] text-[13px] text-casevo-forest cursor-pointer">
          <input
            type="checkbox"
            role="switch"
            checked={readReceipt}
            onChange={(e) => setReadReceipt(e.target.checked)}
            disabled={sending}
            className="sr-only peer"
          />
          <span aria-hidden="true" className={`relative w-8 h-[18px] rounded-full transition-colors peer-focus-visible:ring-2 peer-focus-visible:ring-[var(--casevo-focus)] ${readReceipt ? 'bg-casevo-primary' : 'bg-casevo-border'}`}>
            <span className={`absolute top-[2px] w-[14px] h-[14px] rounded-full bg-casevo-surface transition-[left] ${readReceipt ? 'left-[16px]' : 'left-[2px]'}`} />
          </span>
          Request read receipt
        </label>
      </div>

      {/* Error + sending indicator. */}
      {(error || sending) && (
        <div className="shrink-0 space-y-1">
          {error && <p className="m-0 text-[13px] text-casevo-danger" role="alert">{error}</p>}
          {sending && (
            <p className="m-0 text-[12.5px] text-casevo-mid flex items-center gap-2" aria-live="polite">
              <span className="inline-block w-3 h-3 rounded-full border-2 border-casevo-mid border-t-transparent animate-spin motion-reduce:animate-none" aria-hidden="true" />
              {usesUploadSession ? 'Uploading attachments and sending…' : 'Sending…'}
            </p>
          )}
        </div>
      )}
    </ModalFrame>
    <TimerLeaveDialog
      isOpen={leavePrompt !== null}
      seconds={currentSeconds}
      running={isTimerRunning}
      saveLabel="Save as draft"
      saveHint="Saving keeps this email as a draft with its recorded time. Discard throws away both."
      busy={savingDraft}
      onSave={() => { void handleLeaveSave() }}
      onDiscard={handleLeaveDiscard}
      onKeep={handleLeaveKeep}
    />
    <NonBillableConfirm
      isOpen={confirmNoCase}
      confirmLabel="Send as non-billable"
      onConfirm={() => { setConfirmNoCase(false); void handleSend({ nonBillableOk: true }) }}
      onCancel={() => setConfirmNoCase(false)}
    />
    </>
  )
}

export default ComposeEmailModal
